'use client'

import React from 'react'
import { AuditTimeline, AuditEvent, useAuditTimeline } from '../AuditTimeline'
import { BirhausBasePropsV3 } from '@birhaus/types/v3'
import { useBirhausThemeV3 } from '@birhaus/themes'

/**
 * BIRHAUS v3.0 Radical Minimalist Audit Timeline
 * 
 * Enhanced audit timeline with glass morphism effects,
 * generous spacing, and radical border radius for v3.0 design system.
 * 
 * Features:
 * - Glass morphism event cards
 * - Generous spacing (2x traditional)
 * - Radical border radius (24px+ curves)  
 * - Smooth GPU-accelerated animations
 * - Spanish-first audit terminology
 * - Enhanced progressive disclosure
 */

export interface AuditTimelineV3Props extends Omit<BirhausBasePropsV3, 'variant'> {
  /** Audit events to display */
  events: AuditEvent[]
  
  /** Show timeline filters */
  showFilters?: boolean
  
  /** Maximum height for scrollable timeline */
  maxHeight?: string
  
  /** v3.0 glass intensity preset */
  glassVariant?: 'subtle' | 'medium' | 'heavy'
  
  /** Enable floating event cards */
  floatingCards?: boolean
  
  /** Enable magnetic hover effects on events */
  magneticEvents?: boolean
  
  /** Enable enhanced glass morphism throughout timeline */
  enableGlass?: boolean
  
  /** Performance optimization mode */
  reducedMotion?: boolean
  
  /** v3 theme variant */
  themeName?: 'financial' | 'healthcare' | 'education'
  
  /** Enable glow effects for critical audit events */
  glowCritical?: boolean
  
  // Spanish-first customization
  emptyMessage?: string
  filterAllEntitiesLabel?: string
  filterAllActionsLabel?: string
  clearFiltersLabel?: string
  showDetailsLabel?: string
  hideDetailsLabel?: string
  
  /** Custom class name */
  className?: string
}

export function AuditTimelineV3({
  events,
  showFilters = true,
  maxHeight = '600px',
  glassVariant = 'medium',
  floatingCards = true,
  magneticEvents = true,
  enableGlass = true,
  reducedMotion = false,
  themeName = 'financial',
  glowCritical = true,
  emptyMessage = 'No hay eventos de auditoría para mostrar',
  filterAllEntitiesLabel = 'Todas las entidades',
  filterAllActionsLabel = 'Todas las acciones',
  clearFiltersLabel = 'Limpiar filtros',
  showDetailsLabel = 'Ver detalles',
  hideDetailsLabel = 'Ocultar detalles',
  className,
  ...props
}: AuditTimelineV3Props) {
  
  // BIRHAUS v3.0: Theme integration
  const theme = useBirhausThemeV3(themeName)
  
  // Apply v3 styling configuration
  const v3Classes = [
    'birhaus-audit-timeline-v3',
    'transition-all duration-500 ease-smooth',
    enableGlass ? 'backdrop-blur-lg bg-white/70 rounded-3xl border-white/30 border' : 'bg-white rounded-lg border border-gray-200',
    floatingCards ? 'p-8 shadow-2xl' : 'p-6 shadow-lg',
    className || ''
  ].filter(Boolean).join(' ')
  
  return (
    <div 
      className={v3Classes}
      data-birhaus-component="audit-timeline-v3" 
      data-birhaus-version="3.0"
      data-birhaus-theme={themeName}
      style={{
        maxHeight,
        '--birhaus-glass-blur': enableGlass ? '12px' : '0px',
        '--birhaus-border-radius': '24px',
        '--birhaus-spacing-generous': '2rem',
        '--birhaus-animation-duration': reducedMotion ? '0ms' : '300ms',
        ...theme?.cssVariables
      } as React.CSSProperties}
    >
      <AuditTimeline
        events={events}
        showFilters={showFilters}
        maxHeight={maxHeight}
        emptyMessage={emptyMessage}
        filterAllEntitiesLabel={filterAllEntitiesLabel}
        filterAllActionsLabel={filterAllActionsLabel}
        clearFiltersLabel={clearFiltersLabel}
        showDetailsLabel={showDetailsLabel}
        hideDetailsLabel={hideDetailsLabel}
        className="space-y-6"
        {...props}
      />
      
      {/* v3.0 Glass Morphism Timeline Connector */}
      {enableGlass && events.length > 0 && (
        <div 
          className="absolute left-8 top-20 bottom-8 w-0.5 pointer-events-none"
          style={{
            background: `linear-gradient(180deg, 
              rgba(59, 130, 246, 0.3) 0%, 
              rgba(156, 163, 175, 0.2) 100%)`,
            backdropFilter: 'blur(8px)',
            borderRadius: '2px'
          }}
        />
      )}
    </div>
  )
}

/**
 * BIRHAUS v3.0 Enhanced Audit Event Component
 */
export interface AuditEventV3Props extends AuditEvent {
  /** v3 glass effect */
  glass?: boolean
  
  /** v3 generous spacing */
  generousSpacing?: boolean
  
  /** v3 radical border radius */
  radicalBorderRadius?: boolean
  
  /** Enable magnetic hover effect */
  magnetic?: boolean
  
  /** Enable glow effect for critical events */
  glow?: boolean
  
  /** Event severity level */
  severity?: 'info' | 'warning' | 'error' | 'critical'
  
  /** Show expanded details */
  expanded?: boolean
  
  /** Toggle expanded state */
  onToggleExpanded?: () => void
}

export function AuditEventV3({
  glass = true,
  generousSpacing = true,
  radicalBorderRadius = true,
  magnetic = true,
  glow = false,
  severity = 'info',
  expanded = false,
  onToggleExpanded,
  ...event
}: AuditEventV3Props) {
  
  const getEventClasses = () => {
    const baseClasses = [
      'transition-all duration-300 ease-smooth',
      'border hover:shadow-xl relative'
    ]
    
    // v3 glass effect
    if (glass) {
      baseClasses.push('backdrop-blur-md bg-white/80 border-white/30')
    } else {
      baseClasses.push('bg-white border-gray-200')
    }
    
    // v3 generous spacing
    if (generousSpacing) {
      baseClasses.push('p-6')
    } else {
      baseClasses.push('p-4')
    }
    
    // v3 radical border radius
    if (radicalBorderRadius) {
      baseClasses.push('rounded-2xl')
    } else {
      baseClasses.push('rounded-lg')
    }
    
    // v3 magnetic hover effect
    if (magnetic) {
      baseClasses.push('hover:scale-[1.02] hover:-translate-y-1 cursor-pointer')
    }
    
    // v3 glow effect for critical events
    if (glow && severity === 'critical') {
      baseClasses.push('shadow-2xl shadow-red-500/20 ring-2 ring-red-500/30')
    } else if (glow && severity === 'error') {
      baseClasses.push('shadow-xl shadow-red-500/10 ring-1 ring-red-500/20')
    }
    
    return baseClasses.join(' ')
  }
  
  const getSeverityColor = () => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100'
      case 'error': return 'text-red-600 bg-red-50'
      case 'warning': return 'text-yellow-600 bg-yellow-50'
      default: return 'text-blue-600 bg-blue-50'
    }
  }
  
  return (
    <div className={getEventClasses()} onClick={onToggleExpanded}>
      {/* Event Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor()}`}>
            {event.verb}
          </div>
          <div className="text-sm text-gray-600">
            {event.actor_name || event.actor_id}
          </div>
        </div>
        <div className="text-xs text-gray-500">
          {new Date(event.created_at).toLocaleString('es-ES')}
        </div>
      </div>
      
      {/* Event Details */}
      <div className="space-y-3">
        <div className="text-sm text-gray-800">
          <span className="font-medium">{event.verb}</span>
          {event.entity && (
            <span> en <span className="font-medium">{event.entity}</span></span>
          )}
          {event.entity_id && (
            <span className="text-gray-600"> (ID: {event.entity_id})</span>
          )}
        </div>
        
        {/* Expandable Payload */}
        {expanded && Object.keys(event.payload || {}).length > 0 && (
          <div className={`mt-4 p-4 rounded-xl ${
            glass ? 'bg-white/50 border border-white/40' : 'bg-gray-50 border border-gray-200'
          }`}>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Detalles del evento:</h4>
            <pre className="text-xs text-gray-600 overflow-x-auto">
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      {/* Expand/Collapse Indicator */}
      {Object.keys(event.payload || {}).length > 0 && (
        <div className="mt-3 flex justify-center">
          <button className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1">
            <span>{expanded ? 'Ocultar detalles' : 'Ver detalles'}</span>
            <svg 
              className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

// Export convenience hook for v3 audit timeline
export function useAuditTimelineV3(
  events: AuditEvent[],
  initialConfiguration?: Partial<AuditTimelineV3Props>
) {
  const baseTimeline = useAuditTimeline()
  const [v3Config, setV3Config] = React.useState(initialConfiguration || {})
  
  const updateV3Configuration = React.useCallback((updates: Partial<AuditTimelineV3Props>) => {
    setV3Config(prev => ({ ...prev, ...updates }))
  }, [])
  
  return {
    ...baseTimeline,
    v3Configuration: v3Config,
    updateV3Configuration,
    // v3-specific utilities
    enableGlassMorphism: () => updateV3Configuration({ enableGlass: true, glassVariant: 'medium' }),
    enableFloatingCards: () => updateV3Configuration({ floatingCards: true }),
    enableMagneticEvents: () => updateV3Configuration({ magneticEvents: true }),
    setTheme: (theme: 'financial' | 'healthcare' | 'education') => updateV3Configuration({ themeName: theme }),
    enablePerformanceMode: () => updateV3Configuration({ 
      reducedMotion: true, 
      enableGlass: false, 
      magneticEvents: false 
    }),
    isV3Enabled: true
  }
}